import { getNodeSize } from "@features/builder/graph/nodes/NodeGroupResizable/getNodeSize"
import { AppNode, GroupNode } from "@features/builder/graph/types"
import {
  expandedGroupMinHeight,
  expandedGroupMinWidth,
  groupPadding,
} from "@features/builder/helpers/nodes"
import { nodesGroupMapper } from "@features/builder/helpers/nodesGroupMapper"
import { NodeResizeControl, OnResizeEnd, ShouldResize, useReactFlow } from "@xyflow/react"
import { OnResizeStart, ResizeControlVariant } from "@xyflow/system"
import { useRef } from "react"
import css from "./NodeGroupResizable.module.css"

export const NodeResizeLeft = ({ node }: { node: GroupNode }) => {
  const { getNodes, getNode, updateNode } = useReactFlow<AppNode>()
  const constrainedSizeRef = useRef<{ x: number; width: number } | null>(null)

  const handleResizeStart: OnResizeStart = (_event, _resizedNode) => {
    const mapper = nodesGroupMapper(getNodes())
    const parents = mapper.childToParentGroupsIdsMapDeep.get(node.id) ?? []
    constrainedSizeRef.current = null

    //  @TODO (Bartek Laskowski) 2025-08-19: do not extend due to bug that messing x and y of parents
    const expandParent = false

    for (const parentId of parents) {
      updateNode(parentId, {
        expandParent,
      })
    }
    updateNode(node.id, {
      expandParent,
    })
  }

  const handleResizeEnd: OnResizeEnd = (_event, resizedNode) => {
    const mapper = nodesGroupMapper(getNodes())
    const parents = mapper.childToParentGroupsIdsMapDeep.get(node.id) ?? []

    const finalWidth =
      constrainedSizeRef.current?.width ?? node.measured?.width ?? resizedNode.width
    const finalX = constrainedSizeRef.current?.x ?? node.position?.x ?? resizedNode.x
    constrainedSizeRef.current = null

    for (const parentId of parents) {
      const parentNode = mapper.groupsMap.get(parentId)

      const deepthFromResized = parents.length + 1 - Number(parentNode?.data.nestingLevel)
      const { width, height } = getNodeSize(parentNode, deepthFromResized)

      updateNode(parentId, {
        expandParent: false,
        style: { width, height },
        resizing: false,
      })
    }

    updateNode(node.id, {
      expandParent: false,
      style: { width: finalWidth, height: resizedNode.height },
      position: { x: finalX, y: resizedNode.y },
      resizing: false,
    })
  }

  const handleShouldResize: ShouldResize = (_event, resizedNode) => {
    const mapper = nodesGroupMapper(getNodes())
    const subGroups = mapper.parentToChildGroupsIdsMap.get(node.id) ?? []
    const subNodes = mapper.groupToNodesIdsMap.get(node.id) ?? []

    const freshNode = getNode(node.id)

    // SUB-NODES and SUB-GROUPS
    for (const childNodeId of [...subNodes, ...subGroups]) {
      const childNode = mapper.allMap.get(childNodeId)

      if (freshNode?.resizing && groupPadding >= Number(childNode?.position?.x)) {
        return false
      }
    }

    constrainedSizeRef.current = {
      width: resizedNode.width,
      x: resizedNode.x,
    }

    return true
  }

  return (
    <NodeResizeControl
      position="left"
      className={css.resizeLine}
      variant={ResizeControlVariant.Line}
      minWidth={expandedGroupMinWidth}
      minHeight={expandedGroupMinHeight}
      autoScale={true}
      shouldResize={handleShouldResize}
      onResizeStart={handleResizeStart}
      onResizeEnd={handleResizeEnd}
    />
  )
}
